/**
 * A viewer splat as the ported renderer sees it: the engine's uncompressed
 * GSplatResource (transformA/B, splatColor, SH textures) plus the identity
 * versions of the editor's per-gaussian buffers — every instance reads its own
 * row, no flags, palette entry 0 (identity transform, identity grade).
 */

import {
    BUFFERUSAGE_COPY_DST,
    BUFFERUSAGE_COPY_SRC,
    PIXELFORMAT_RGBA32F,
    Entity,
    GraphicsDevice,
    GSplatResource,
    StorageBuffer,
    Texture
} from 'playcanvas';

import type { Instances, Splat } from './types';

let nextUid = 1;

const identityInstances = (device: GraphicsDevice, count: number): Instances => {
    const usage = BUFFERUSAGE_COPY_SRC | BUFFERUSAGE_COPY_DST;
    const source = new Uint32Array(Math.max(1, count));
    for (let i = 0; i < count; i++) source[i] = i;
    const flags = new Uint32Array(Math.max(1, Math.ceil(count / 4)));
    const palette = new Uint32Array(Math.max(1, count));

    const instanceSource = new StorageBuffer(device, source.byteLength, usage);
    const instanceFlags = new StorageBuffer(device, flags.byteLength, usage);
    const instancePalette = new StorageBuffer(device, palette.byteLength, usage);
    (instanceSource as unknown as { write(o: number, d: ArrayBufferView, s?: number, n?: number): void }).write(0, source, 0, source.length);
    (instanceFlags as unknown as { write(o: number, d: ArrayBufferView, s?: number, n?: number): void }).write(0, flags, 0, flags.length);
    (instancePalette as unknown as { write(o: number, d: ArrayBufferView, s?: number, n?: number): void }).write(0, palette, 0, palette.length);

    return {
        count,
        byteSize: source.byteLength + flags.byteLength + palette.byteLength,
        numSelected: 0,
        instanceSource,
        instanceFlags,
        instancePalette
    };
};

// palette lookups early-out on index 0, so the textures only have to exist
const paletteTexture = (device: GraphicsDevice, name: string) => new Texture(device, {
    name,
    width: 4,
    height: 1,
    format: PIXELFORMAT_RGBA32F,
    mipmaps: false
});

class ViewerSplat implements Splat {
    uid = nextUid++;
    visible = true;
    entity: Entity;
    resource: GSplatResource;
    instances: Instances;
    transformPalette: { texture: Texture };
    colorPalette: { texture: Texture };
    /** true when the resource was built here (from a decompressed SOG) and must be destroyed with the splat */
    private readonly ownsResource: boolean;

    constructor(device: GraphicsDevice, entity: Entity, resource: GSplatResource, ownsResource: boolean) {
        this.entity = entity;
        this.resource = resource;
        this.ownsResource = ownsResource;
        this.instances = identityInstances(device, resource.numSplats);
        this.transformPalette = { texture: paletteTexture(device, 'viewerTransformPalette') };
        this.colorPalette = { texture: paletteTexture(device, 'viewerColorPalette') };
    }

    get numSplats() {
        return this.resource.numSplats;
    }

    destroy() {
        this.instances.instanceSource.destroy();
        this.instances.instanceFlags.destroy();
        this.instances.instancePalette.destroy();
        this.transformPalette.texture.destroy();
        this.colorPalette.texture.destroy();
        if (this.ownsResource) this.resource.destroy();
    }
}

export { ViewerSplat };
